import { cn } from '../../lib/utils.js';

type SkeletonVariant = 'text' | 'avatar' | 'card' | 'table-row';

interface SkeletonProps {
  variant?: SkeletonVariant;
  className?: string;
}

const VARIANT_CLASSES: Record<SkeletonVariant, string> = {
  text: 'h-4 w-full',
  avatar: 'h-10 w-10 rounded-full',
  card: 'h-32 w-full',
  'table-row': 'h-10 w-full',
};

export function Skeleton({ variant = 'text', className }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded bg-muted',
        VARIANT_CLASSES[variant],
        className,
      )}
    />
  );
}

/** Full-page skeleton that matches a typical list/table page layout */
export function PageSkeleton() {
  return (
    <div className="flex min-h-screen flex-col bg-background p-6" aria-busy="true" aria-label="Loading page">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton variant="text" className="w-48 h-7" />
          <Skeleton variant="text" className="w-72 h-4" />
        </div>
        <Skeleton variant="text" className="w-32 h-9" />
      </div>

      {/* Toolbar / filters */}
      <div className="mb-4 flex gap-3">
        <Skeleton variant="text" className="w-48 h-9" />
        <Skeleton variant="text" className="w-36 h-9" />
        <Skeleton variant="text" className="w-36 h-9" />
      </div>

      {/* Table skeleton */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {/* Header row */}
        <div className="flex gap-4 border-b border-border px-4 py-3">
          {[140, 200, 120, 120, 100, 100].map((w, i) => (
            <Skeleton key={i} variant="text" className={`h-4 w-[${w}px]`} />
          ))}
        </div>
        {/* Data rows */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex gap-4 border-b border-border px-4 py-3 last:border-b-0">
            {[140, 200, 120, 120, 100, 100].map((w, j) => (
              <Skeleton key={j} variant="table-row" className={`h-4 w-[${w}px]`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** KPI card skeleton for dashboard pages */
export function KpiCardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-3">
      <Skeleton variant="text" className="w-24 h-3" />
      <Skeleton variant="text" className="w-32 h-8" />
    </div>
  );
}
